import { type ExecuteEventFn } from 'react-i13n';
import isError from 'lodash/isError';
import isString from 'lodash/isString';

import { I13N_CATEGORY_APP, I13N_TYPE_EXCEPTION } from 'Lib/constants/i13nSpec';
import getI13nUtils from 'Lib/i13n/getI13nUtils';

// filter out cross origin error, probably use array of regex
const IGNORED_ERROR = 'script error';

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error#Error_types
const ERROR_TYPES_RE =
  /^(?:[Uu]ncaught (?:exception: )?)?(?:((?:Eval|Internal|Range|Reference|Syntax|Type|URI|)Error): )?(.*)$/i;

/**
 * Class to handle window error tracking
 */
class ErrorTracking {
  executeEvent: ExecuteEventFn;

  constructor() {
    this.executeEvent = getI13nUtils().executeEvent;
  }

  init = (): void => {
    window.addEventListener('error', this.handleError);
  };

  destroy = (): void => {
    window.removeEventListener('error', this.handleError);
  };

  // largely modify from https://github.com/getsentry/sentry-javascript/blob/master/packages/browser/src/integrations/globalhandlers.ts
  handleError = (event: ErrorEvent): void => {
    const { message: msg, filename: url = '' } = event;

    let { name } = event as unknown as Error;
    let message: string = msg;
    let error: Error = new Error('error');
    const { hostname } = location;

    if (!__DEVELOPMENT__ && !url.includes(hostname)) {
      // don't track third party script
      return;
    }

    if (event.error && isError(event.error)) {
      // for type ErrorEvent
      ({ error } = event);
    }

    if (isError(event)) {
      // for type Error
      // error.name: ReferenceError
      // error.message: foo is not defined
      ({ message, name } = event);
      error = event;
    } else if (isString(msg)) {
      const groups = msg.match(ERROR_TYPES_RE);
      if (groups) {
        [, name, message] = groups;
      }
    }

    const errorMessage = `${name} ${message}`;
    // skip crossorigin error
    if (errorMessage.toLowerCase().includes(IGNORED_ERROR)) {
      return;
    }

    this.executeEvent(I13N_TYPE_EXCEPTION, {
      category: I13N_CATEGORY_APP,
      label: errorMessage,
      error: {
        error,
        level: 4, // critical
      },
    });
  };
}

export default ErrorTracking;
